import { useState } from 'react'
import { fmtDate } from '../services/dates.js'

export default function ActivityModal({ area, actKey, actLabel, plannedDate, currentRecord, availableProducts, isAdmin, onSave, onClose }) {
  const [done, setDone] = useState(currentRecord?.done ?? false)
  const [used, setUsed] = useState(currentRecord?.used_products?.map(p => p.product_name) ?? [])
  const [notes, setNotes] = useState(currentRecord?.notes ?? '')
  const [performedAt, setPerformedAt] = useState(currentRecord?.performed_at ?? '')
  const [editedPlannedDate, setEditedPlannedDate] = useState(plannedDate ?? '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  function toggleProduct(p) {
    setUsed(prev => prev.includes(p) ? prev.filter(u => u !== p) : [...prev, p])
  }

  async function handleSave() {
    setSaving(true)
    const data = { done, usedProducts: used, notes, performedAt: performedAt || null }
    if (isAdmin) {
      data.plannedDate = editedPlannedDate || null
    }
    setSaveError('')
    try { await onSave(data) }
    catch (e) { setSaveError(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 440, boxShadow: '0 30px 70px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
          Registrar atividade
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#14532d', marginBottom: 2 }}>{area.name}</div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{actLabel}</div>

        {/* Data prevista — editável para admin, read-only para operador */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
            Data prevista
          </div>
          {isAdmin ? (
            <input
              type="date"
              value={editedPlannedDate}
              onChange={e => setEditedPlannedDate(e.target.value)}
              style={{
                padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10,
                fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%',
              }}
            />
          ) : (
            <div style={{
              display: 'inline-block', background: '#f1f5f9', borderRadius: 8,
              padding: '6px 14px',
              fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 600, color: '#334155',
            }}>{plannedDate ? fmtDate(plannedDate) : '—'}</div>
          )}
        </div>

        {/* Toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', marginBottom: 28 }}>
          <div onClick={() => setDone(d => !d)} style={{
            width: 52, height: 30, borderRadius: 999,
            background: done ? '#16a34a' : '#cbd5e1',
            position: 'relative', flexShrink: 0, transition: 'background 0.2s',
          }}>
            <div style={{
              position: 'absolute', top: 3, left: done ? 25 : 3,
              width: 24, height: 24, borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            }} />
          </div>
          <span style={{ fontSize: 15, fontWeight: 700, color: done ? '#15803d' : '#64748b' }}>
            {done ? 'Atividade realizada ✓' : 'Marcar como realizada'}
          </span>
        </label>

        {/* Performed at */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
            Data de realização
          </div>
          <input
            type="date"
            value={performedAt}
            onChange={e => setPerformedAt(e.target.value)}
            style={{
              padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10,
              fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%',
            }}
          />
        </div>

        {/* Products */}
        {availableProducts.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
              Produtos utilizados
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {availableProducts.map(p => {
                const checked = used.includes(p)
                return (
                  <button key={p} onClick={() => toggleProduct(p)} style={{
                    padding: '7px 16px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', border: checked ? '2px solid #16a34a' : '2px solid #e2e8f0',
                    background: checked ? '#dcfce7' : '#fff',
                    color: checked ? '#15803d' : '#64748b', transition: 'all 0.15s',
                  }}>{checked ? '✓ ' : ''}{p}</button>
                )
              })}
            </div>
          </>
        )}

        {/* Notes */}
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Observações (opcional)"
          rows={2}
          style={{
            width: '100%', padding: '10px 14px', marginBottom: 24,
            border: '2px solid #e2e8f0', borderRadius: 10,
            fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none',
          }}
        />

        {saveError && (
          <div style={{ background: '#fee2e2', color: '#7f1d1d', padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
            Erro: {saveError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px', border: '2px solid #e2e8f0', borderRadius: 10,
            background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#64748b',
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, padding: '12px', border: 'none', borderRadius: 10,
            background: '#15803d', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700,
          }}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
